// src/components/profile/roi/AIInsightPanel.tsx
// Iteration 6: Premium AI Insight Panel with sparkle icon and gradient border

import React, { useState } from 'react';
import { Sparkles, Info } from 'lucide-react';

interface AIInsightPanelProps {
  recommendation?: {
    status?: string;
    message?: string;
    priority?: string;
    rationale?: string;
  } | null;
  insight?: string;
}

export default function AIInsightPanel({ recommendation, insight }: AIInsightPanelProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  const displayText = insight || recommendation?.message || recommendation?.rationale || 
    'AI-generated insight based on scenario assumptions & sensitivity modeling';
  
  if (!displayText) return null;
  
  return (
    <div 
      className="relative rounded-xl p-6 bg-white border transition-all duration-300 group"
      style={{
        borderImage: isHovered 
          ? 'linear-gradient(135deg, #18365E 0%, #9333EA 100%) 1'
          : 'linear-gradient(135deg, rgba(24, 54, 94, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%) 1',
        borderWidth: '2px',
        borderStyle: 'solid',
        boxShadow: isHovered 
          ? '0 4px 12px rgba(24, 54, 94, 0.15), 0 0 0 1px rgba(147, 51, 234, 0.1)' 
          : '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title="AI-generated insight based on scenario assumptions & sensitivity modeling"
    >
      {/* Gradient border effect */}
      <div 
        className="absolute inset-0 rounded-xl pointer-events-none transition-opacity duration-300"
        style={{
          background: isHovered 
            ? 'linear-gradient(135deg, rgba(24, 54, 94, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(24, 54, 94, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%)',
          opacity: isHovered ? 1 : 0.5
        }}
      ></div>
      
      <div className="relative z-10">
        {/* Header with sparkle icon */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative">
            <Sparkles className="w-5 h-5 text-[#18365E] animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-[#9333EA] opacity-60" />
            </div>
          </div>
          <h4 className="text-sm font-semibold text-gray-900">
            AI Insight
          </h4>
          <div className="ml-auto">
            <Info className="w-4 h-4 text-gray-400 group-hover:text-[#18365E] transition-colors" />
          </div>
        </div>
        
        {/* Insight content */}
        <p className="text-sm text-gray-700 leading-relaxed">
          {displayText}
        </p>
        
        {/* Hover tooltip */}
        {isHovered && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="font-semibold mb-1">AI-Generated Insight</div>
            <div className="text-gray-300">
              Based on scenario assumptions & sensitivity modeling
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

